import { useEffect, useRef, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { maxListPanelWidth } from '../../utils/listPanelWidth'

interface DragDividerProps {
  width: number
  onWidthChange: (width: number) => void
}

export default function DragDivider({ width, onWidthChange }: DragDividerProps) {
  const windowWidth = useWindowWidth()
  const maxWidth = maxListPanelWidth(windowWidth)

  // handlePointerDown adds pointermove/pointerup listeners directly to `document`, cleaned up
  // only from within onUp itself — if this component unmounts mid-drag (e.g. the Map/List
  // toggle switches away from List while the pointer button is still held), those listeners
  // stayed attached to `document` referencing this unmounted render's stale onWidthChange
  // closure, until some unrelated future pointerup fired anywhere on the page. Tracking the
  // active pair in a ref lets an unmount effect remove them too.
  const activeListenersRef = useRef<{ onMove: (e: PointerEvent) => void; onUp: () => void } | null>(null)

  useEffect(() => {
    return () => {
      if (activeListenersRef.current) {
        document.removeEventListener('pointermove', activeListenersRef.current.onMove)
        document.removeEventListener('pointerup', activeListenersRef.current.onUp)
        activeListenersRef.current = null
      }
    }
  }, [])

  function handlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = width

    function onMove(moveEvent: PointerEvent) {
      const delta = moveEvent.clientX - startX
      const raw = startWidth + delta
      // Reads window.innerWidth live rather than the `maxWidth` above so a resize mid-drag is
      // respected, but goes through the same helper so the pointer path and the keyboard path
      // (ArrowRight, which clamps to `maxWidth`) share one ceiling.
      const clamped = Math.max(32, Math.min(raw, maxListPanelWidth(window.innerWidth)))
      onWidthChange(clamped < 80 ? 32 : clamped)
    }

    function onUp() {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      activeListenersRef.current = null
    }

    activeListenersRef.current = { onMove, onUp }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const step = 24
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onWidthChange(Math.max(32, width - step))
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      onWidthChange(Math.min(maxWidth, width + step))
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- this is the WAI-ARIA "focusable separator" (window splitter) pattern: role="separator" + aria-valuenow/min/max is meant to be focusable and keyboard-operable, jsx-a11y's static rule doesn't know that variant
    <div
      role="separator"
      aria-label="Drag to resize list panel"
      aria-orientation="vertical"
      aria-valuenow={Math.round(width)}
      aria-valuemin={32}
      aria-valuemax={Math.round(maxWidth)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- same focusable-separator pattern, see comment above
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      style={{
        width: 10,
        flexShrink: 0,
        cursor: 'col-resize',
        background: '#f1f5f9',
        borderLeft: '1px solid #e2e8f0',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
      }}
    >
      <div data-testid="drag-grip" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ width: 2, height: 2, background: 'var(--text-muted)', borderRadius: '50%' }} />
        ))}
      </div>
    </div>
  )
}
